import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import numeral from 'numeral';
import CommonStrings from 'resources/common-strings';

//Sets the width of the popover box.  This is a static width, so the length of the content within the box
//will not affect how big the box is.  If wider content is needed, we either need to wrap that content or
//make this box wider.
const POPOVER_WIDTH = 322;

export function buildTestEventSection(termId, testEvents) {
    let eventSections = [];
    if (!testEvents) {
        return eventSections;
    }
    const testEvent = testEvents.find((t) => t.get('termNumber') == termId);
    if (!!testEvent) {
        let mainSection = {};
        const ritRow = {
            label: 'RIT Score',
            data: testEvent.get('score'),
        };
        const errorRow = {
            label: 'Standard Error',
            data: '+/- ' + testEvent.get('standardError'),
        };
        const testDuration = {
            label: 'Test Duration',
            data: Math.round(testEvent.get('testDurationMs') / 1000 / 60) + ' mins',
        };
        const responseDisengagedPercentage = {
            label: CommonStrings.disengagement.disengagementPercentage,
            data: testEvent.get('responseDisengagedPercentage')
                ? Math.round(testEvent.get('responseDisengagedPercentage')) + '%'
                : 'N/A',
        };
        const impactOfDisengagement = {
            label: CommonStrings.disengagement.impactOfDisengagement,
            data: testEvent.get('impactOfDisengagement') ? Math.round(testEvent.get('impactOfDisengagement')) : 'N/A',
        };
        let valueWithOrdinal = numeral(testEvent.getIn(['statusNorms', 'studentPercentile'], 0)).format('0o');
        const achievementPercentile = {
            label: 'Achievement Percentile',
            data: valueWithOrdinal,
        };
        mainSection = {
            name: 'RIT Data-' + termId,
            rows: [
                ritRow,
                errorRow,
                testDuration,
                responseDisengagedPercentage,
                impactOfDisengagement,
                achievementPercentile,
            ],
        };
        eventSections = eventSections.concat(mainSection);
        const instructionalAreas = testEvent.get('goals');
        if (!!instructionalAreas) {
            let instructionalAreasSection = {};
            let iaRows = [];
            instructionalAreas.forEach((ia) => {
                const iaRow = {
                    label: ia.get('name'),
                    data: ia.get('score'),
                };
                iaRows = iaRows.concat(iaRow);
            });
            instructionalAreasSection = {
                name: 'Instructional Area Data-' + termId,
                rows: iaRows.sort((r1, r2) => r1.data - r2.data),
            };
            eventSections = eventSections.concat(instructionalAreasSection);
        } // if (!!instructionalAreas)
    } // if (!!testEvent)
    return eventSections;
}

export function buildRitScoreSection(termId, termGoal, projectedGrowth, index, predictedGrowth, lastTestEventTerm) {
    let eventSections = [];
    let mainSection = {};
    let sectionRows = [];

    if (termGoal) {
        let goalLabel = predictedGrowth ? 'Growth Goal RIT Score' : 'Performance Goal RIT Score';
        const goalRITRow = {
            label: goalLabel,
            data: termGoal,
        };
        sectionRows = sectionRows.concat(goalRITRow);
    }

    if (projectedGrowth && termId > lastTestEventTerm) {
        let projectedScoreOfTerm = projectedGrowth[index].y;
        if (projectedScoreOfTerm) {
            const projectedRITRow = {
                label: 'RIT Score if Projected Growth met',
                data: projectedScoreOfTerm,
            };
            sectionRows = sectionRows.concat(projectedRITRow);
        }
    }

    if (sectionRows.length > 0) {
        mainSection = {
            name: 'Projected RIT Score-' + termId,
            rows: sectionRows,
        };
    }

    if (Object.keys(mainSection).length > 0) eventSections = eventSections.concat(mainSection);

    return eventSections;
}

export function buildDistrictGradeLevelMeanAndTypicalGrowthSection(termId, districtAverages, typicalGrowth, index) {
    if (!districtAverages && !typicalGrowth) {
        return [];
    }
    let eventSections = [];
    let mainSection = {};
    let districtMean = districtAverages ? districtAverages[index].y : null;
    let typicalPerformance = typicalGrowth ? typicalGrowth[index].y : null;
    let sectionRows = [];

    if (districtMean) {
        const distictMeanRow = {
            label: 'District Grade Level Mean',
            data: districtMean,
        };
        sectionRows = sectionRows.concat(distictMeanRow);
    }

    if (typicalPerformance) {
        const typicalPerformanceRow = {
            label: 'Average Achievement',
            data: typicalPerformance,
        };
        sectionRows = sectionRows.concat(typicalPerformanceRow);
    }

    if (sectionRows.length > 0) {
        mainSection = {
            name: 'District Data-' + termId,
            rows: sectionRows,
        };
    }

    if (Object.keys(mainSection).length > 0) eventSections = eventSections.concat(mainSection);

    return eventSections;
}

export function buildLinkingStudiesSection(
    termId,
    collegeLinkingStudy,
    stateLinkingStudy,
    index,
    termGoal,
    projectedGrowth,
    predictedGrowth
) {
    let eventSections = [];
    let mainSection = {};
    let sectionRows = [];
    let growthLabel = predictedGrowth
        ? 'Projected proficiency if Growth Goal met'
        : 'Projected proficiency if Performance Goal met';
    let linkingLabel = termGoal ? growthLabel : 'Projected proficiency if Projected Growth met';

    const projectedGrowthScore = projectedGrowth ? projectedGrowth[index].y : null;
    const scoreToCompare = termGoal ? termGoal : projectedGrowthScore;

    if (scoreToCompare) {
        if (collegeLinkingStudy) {
            collegeLinkingStudy.forEach((study) => {
                if (study.get('termNumber') == termId && study.get('cutScores').count() > 0) {
                    let collegeCutScore = study
                        .get('cutScores')
                        .find((m) => m.get('ritMin') <= scoreToCompare && m.get('ritMax') >= scoreToCompare);
                    if (collegeCutScore) {
                        const collegeLinkingStudyRow = {
                            label: study.get('study'),
                            data: collegeCutScore.get('category'),
                        };
                        sectionRows = sectionRows.concat(collegeLinkingStudyRow);
                    }
                }
            });
        }
        if (
            stateLinkingStudy &&
            stateLinkingStudy.get('termNumber') == termId &&
            stateLinkingStudy.get('cutScores').count() > 0
        ) {
            let stateCutScore = stateLinkingStudy
                .get('cutScores')
                .find((m) => m.get('ritMin') <= scoreToCompare && m.get('ritMax') >= scoreToCompare);
            if (stateCutScore) {
                const stateLinkingStudyRow = {
                    label: stateLinkingStudy.get('study'),
                    data: stateCutScore.get('category'),
                };
                sectionRows = sectionRows.concat(stateLinkingStudyRow);
            }
        }
        if (sectionRows.length > 0) {
            mainSection = {
                name: 'Linking Study-' + termId,
                header: linkingLabel,
                rows: sectionRows,
            };
        }
    }

    if (Object.keys(mainSection).length > 0) eventSections = eventSections.concat(mainSection);

    return eventSections;
}

export function togglePopover() {
    Analytics.recordAction(CommonValues.SectionIDs.growthOverTime, CommonValues.GrowthOverTimeActionNames.openPopover);
    const selectedBar = this;
    const chart = selectedBar.series.chart;
    const popoverName = 'popoverBox' + selectedBar.x;
    if (chart[popoverName]) {
        closeCurrentPopover();
    } else {
        showPopover(selectedBar);
    }
}

export function closeCurrentPopover() {
    const chartPopoverInfo = nweaGlobals.chartPopover;
    //Chart might exist, but be empty if we refresh student data
    const chartEmpty =
        chartPopoverInfo && chartPopoverInfo.chart ? Object.keys(chartPopoverInfo.chart).length === 0 : false;
    if (chartPopoverInfo && chartPopoverInfo.popoverName && chartPopoverInfo.chart && !chartEmpty) {
        closePopover(chartPopoverInfo.chart, chartPopoverInfo.popoverName);
        const selectedPoints = chartPopoverInfo.chart.getSelectedPoints();
        selectedPoints.forEach(deselectBar);
    } // if (chartPopoverInfo && chartPopoverInfo.popoverName && chartPopoverInfo.chart)
    nweaGlobals.chartPopover = { popoverName: null, chart: null };
}

export function clearPopovers(selectedBar) {
    // This function can be called in different ways.
    // If called via the event listener for the chart, then 'this' contains the chart object.
    // If called via togglePopover, then 'this' is undefined, and the chart is found through the parameter.
    const chart = !this ? selectedBar.series.chart : this;
    const clickEvent = this ? selectedBar : null;
    let barToClear = selectedBar;
    let userClickedPopoverBox = false;
    const selectedBars = chart.getSelectedPoints();
    selectedBars.forEach((b) => {
        if (!barToClear) {
            barToClear = b;
        }
        const popoverName = 'popoverBox' + b.x;
        if (chart[popoverName]) {
            // If a popover exists, then fade it out, then get rid of it.
            if (clickEvent && clickEvent.chartX) {
                // If the user clicked in the chart area, then check to see if a popover was clicked.
                userClickedPopoverBox =
                    clickEvent.chartX >= chart[popoverName].x &&
                    clickEvent.chartX <= chart[popoverName].x + chart[popoverName].width;
                userClickedPopoverBox =
                    userClickedPopoverBox &&
                    clickEvent.chartY >= chart[popoverName].y &&
                    clickEvent.chartY <= chart[popoverName].y + chart[popoverName].height;
            } // if (clickEvent)
            if (!userClickedPopoverBox) {
                // If a popover was not clicked, then close it out.
                closeCurrentPopover();
            } // if (!userClickedPopoverBox)
        }
    });
    if (!userClickedPopoverBox) {
        const selectedPoints = chart.getSelectedPoints();
        selectedPoints.forEach(deselectBar);
    }
}

function closePopover(chart, popoverName) {
    if (chart[popoverName]) {
        // If a popover exists, then fade it out, then get rid of it.
        chart[popoverName].animate(
            {
                opacity: 0,
            },
            {
                duration: 250,
            },
            function() {
                chart[popoverName].destroy();
                chart[popoverName] = null;
            }
        );
    }
}

export function deselectBar(barToDeselect) {
    barToDeselect.selected = false;
    // Pretty sure we don't need the following line, but leaving it in case not having it breaks something.
    // Removed it in the first place because it caused the chart to re-render, which messed up the test series data point marker.
    // barToDeselect.update(barToDeselect.y);
}

function showPopover(selectedBar) {
    closeCurrentPopover();
    const chart = selectedBar.series.chart;
    const content = buildContent(selectedBar.nweaContent);
    const edgeBarHorizontalAdjustment =
        selectedBar.plotX < POPOVER_WIDTH / 2
            ? POPOVER_WIDTH / 2 - selectedBar.plotX - 10
            : selectedBar.plotX > chart.plotWidth - POPOVER_WIDTH / 2
            ? chart.plotWidth - selectedBar.plotX - POPOVER_WIDTH / 2 + 10
            : 0;
    const popoverName = 'popoverBox' + selectedBar.x;
    chart[popoverName] = chart.renderer.label(
        content,
        selectedBar.plotX + chart.plotLeft - POPOVER_WIDTH / 2 + edgeBarHorizontalAdjustment,
        0,
        'callout',
        selectedBar.plotX + chart.plotLeft,
        chart.plotHeight,
        true,
        false,
        'growth-over-time-popover-box'
    );
    chart[popoverName].attr({
        r: 4,
        fill: 'rgba(0, 0, 0, 0.9)',
        stroke: '#24B3E8',
        'stroke-width': 2,
        zIndex: 200,
        opacity: 0,
    });
    chart[popoverName].css({
        fontFamily: "'Open Sans', Arial, sans-serif",
        fontSize: '1.3rem',
    });
    chart[popoverName].add();
    nweaGlobals.chartPopover = { popoverName, chart };
    const popoverBoundary = chart[popoverName].getBBox();
    let adjustment = popoverBoundary.height / 2;
    if (typeof event !== 'undefined') {
        if (event.chartY) {
            adjustment = event.chartY;
        } else {
            adjustment = event.layerY - 10;
        }
    }
    const verticalAdjustment = adjustment - popoverBoundary.height;
    chart[popoverName].ySetter(verticalAdjustment);
    chart[popoverName].animate(
        {
            opacity: 1,
        },
        {
            duration: 250,
        },
        function() {
            document
                .getElementById('popover-close-box-' + selectedBar.nweaContent.term)
                .addEventListener('click', function() {
                    closeCurrentPopover();
                });
            // setChartDocumentEvents();
        }
    );
}

function buildContent(contentParts) {
    const contentContainer = document.createElement('div');
    contentContainer.className = 'popover-container';
    contentContainer.setAttribute('style', 'width: ' + POPOVER_WIDTH / 10 + 'rem'); // Setting width to force Highcharts to draw correctly

    // Header (Term YYYY) and close box
    const contentHeader = document.createElement('h1');
    contentHeader.id = 'popover-header';
    const headerText = document.createTextNode(contentParts.header);
    contentHeader.appendChild(headerText);
    const closeBox = document.createElement('span');
    closeBox.textContent = '\u00D7';
    closeBox.id = 'popover-close-box-' + contentParts.term;
    contentHeader.appendChild(closeBox);
    contentContainer.appendChild(contentHeader);

    // Distinct sections within the popup
    for (const section of contentParts.sections) {
        const sectionElement = document.createElement('section');
        if (section.header) {
            // Section header, if it exists
            const sectionHeader = document.createElement('h2');
            sectionHeader.className = 'section-header';
            sectionHeader.textContent = section.header;
            sectionElement.appendChild(sectionHeader);
        } // if (section.header)
        for (const sectionRow of section.rows) {
            // Distinct rows within a section
            let sectionRowItem = document.createElement('span');
            sectionRowItem.className = 'data-label';
            sectionRowItem.textContent = sectionRow.label;
            sectionElement.appendChild(sectionRowItem);
            sectionRowItem = document.createElement('span');
            sectionRowItem.className = 'data-value';
            sectionRowItem.textContent = sectionRow.data;
            sectionElement.appendChild(sectionRowItem);
        } // for (const sectionRow of section.rows)
        contentContainer.appendChild(sectionElement);
    } // for (const section of contentParts.sections)

    if (contentParts.allowSetGoal) {
        const sectionElement = document.createElement('section');
        let sectionRowItem = document.createElement('span');
        sectionRowItem.className = 'setGoal';
        sectionRowItem.textContent = 'Set Goal?';
        sectionElement.appendChild(sectionRowItem);
        sectionRowItem = document.createElement('button');
        sectionRowItem.type = 'button';
        sectionRowItem.id = 'setGoal' + contentParts.term;
        sectionRowItem.dataset.term = contentParts.term;
        sectionRowItem.className = 'btn btn-blue btn-default';
        sectionRowItem.textContent = 'GO';
        sectionElement.appendChild(sectionRowItem);
        contentContainer.appendChild(sectionElement);
    }

    return contentContainer.outerHTML; // Returning text since Highcharts is expecting text
}

export default {
    buildTestEventSection,
    buildRitScoreSection,
    buildDistrictGradeLevelMeanAndTypicalGrowthSection,
    buildLinkingStudiesSection,
    togglePopover,
    closeCurrentPopover,
    clearPopovers,
    deselectBar,
};
